import React from "react";
import {usePDF} from "@react-pdf/renderer"
import {buildPdfFileName} from "./DisclosurePDFs/pdfFileName";

import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {PurchaseCustomerDisclosurePDF} from "./DisclosurePDFs/Customer/Purchase/PurchaseCustomerDisclosurePDF";
import {CustomerDisclosurePDF} from "./DisclosurePDFs/Customer/Lease/CustomerDisclosurePDF";
import {useUserContext} from "../../contexts/UserContext";
import {PurchaseDealerDisclosurePDF} from "./DisclosurePDFs/Dealer/Purchase/PurchaseDealerDisclosurePDF";
import {DealerDisclosurePDF} from "./DisclosurePDFs/Dealer/Lease/DealerDisclosurePDF";
import {BaseModal} from "../../components/BaseModal";
import {useThemeStore} from "../../stores/themeStore";
import {useCompanyLogo} from "../../hooks/useCompanyLogo";
import useDidMountEffect from "../../hooks/useDidMountEffect";


export const PDFModal = ({ isOpen, setIsOpen, dealState, isCustomerPDF, ...props } ) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const { profileState } = useUserContext()
    const theme = useThemeStore((state) => state.theme)
    // null until the account's logo is confirmed loadable; until (or unless) it
    // resolves the worksheet simply renders its usual footer, with no logo row.
    // isCompanyLogoLoading gates the one-shot Print/Download actions so a
    // generated PDF never captures the pending-null fallback before the logo probe
    // resolves. The PDF is re-rendered once if the logo settles after opening.
    const {companyLogoUrl, isCompanyLogoLoading} = useCompanyLogo()

    // The BaseModal wrapper carries sizing/positioning only; the .PDFModal card
    // paints its own themed surface, matching the DealRebateModal pattern.
    const wrapperStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        padding: 0,
        width: isTabletOrMobile ? '100vw' : '60vw',
        height: isTabletOrMobile ? '75vh' : '90vh',
    };

    const pdfDisclosure = () => {
        if (isCustomerPDF) {
            if (dealState.is_purchase) {
                return (<PurchaseCustomerDisclosurePDF
                    dealState={dealState}
                    companyName={profileState?.account_name}
                    companyLogoUrl={companyLogoUrl}
                />)
            } else {
                return (
                    <CustomerDisclosurePDF
                        dealState={dealState}
                        companyName={profileState?.account_name}
                        companyLogoUrl={companyLogoUrl}
                    />
                )
            }
        } else {
            if (dealState.is_purchase) {
                return (
                    <PurchaseDealerDisclosurePDF
                        dealState={dealState}
                        companyName={profileState?.account_name}
                        companyLogoUrl={companyLogoUrl}
                    />
                )
            } else {
                return (
                    <DealerDisclosurePDF
                        dealState={dealState}
                        companyName={profileState?.account_name}
                        companyLogoUrl={companyLogoUrl}
                    />
                )
            }
        }
    }

    const pdfDoc = pdfDisclosure()
    const fileName = `${buildPdfFileName(dealState, isCustomerPDF ? 'Customer' : 'Dealer')}.pdf`

    // The one render: preview, download and print all use this blob URL. Each
    // extra react-pdf renderer re-renders the whole document (and refetches /pdf).
    const [pdfInstance, updatePdf] = usePDF({ document: pdfDoc })
    const isPreparing = pdfInstance.loading || isCompanyLogoLoading
    useDidMountEffect(() => updatePdf(pdfDoc), [companyLogoUrl])
    const handlePrint = () => {
        if (!pdfInstance.url) return
        const w = window.open(pdfInstance.url)
        w?.addEventListener('load', () => w.print())
    }

    return (
        <BaseModal
            isOpen={isOpen}
            setIsOpen={() => setIsOpen(null)}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            style={wrapperStyle}
        >
            <div className={'PDFModal'} data-theme={theme}>
                {
                    !isTabletOrMobile &&
                    <React.Fragment>
                        <button
                            type="button"
                            className={'PDFModal__close'}
                            aria-label={'Close'}
                            onClick={() => setIsOpen(null)}
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="6" y1="6" x2="18" y2="18"/>
                                <line x1="18" y1="6" x2="6" y2="18"/>
                            </svg>
                        </button>

                        <div className={'PDFModal__header'}>
                            <span className={'PDFModal__title'}>
                                {isCustomerPDF ? 'Customer' : 'Dealer'} Worksheet
                            </span>
                            <div className={'PDFModal__actions'}>
                                <button
                                    type="button"
                                    className={'PDFModal__action-btn'}
                                    onClick={handlePrint}
                                    disabled={isPreparing}
                                >
                                    Print
                                </button>
                                {/* Explicit download — the browser PDF viewer's own
                                    download button can't name a blob, so this anchor
                                    (download={fileName}) is what fixes the filename. */}
                                <a href={pdfInstance.url || undefined} download={fileName} style={{textDecoration: 'none'}}>
                                    <button
                                        type="button"
                                        className={'PDFModal__action-btn PDFModal__action-btn--primary'}
                                        disabled={isPreparing}
                                    >
                                        {isPreparing ? 'Preparing…' : 'Download'}
                                    </button>
                                </a>
                            </div>
                        </div>

                        <div className={'PDFModal__divider'}/>
                    </React.Fragment>
                }

                <div className={'PDFModal__viewer'}>
                    {/* What react-pdf's PDFViewer renders, fed by the same blob. */}
                    <iframe
                        src={pdfInstance.url ? `${pdfInstance.url}#toolbar=0` : undefined}
                        title={`${isCustomerPDF ? 'Customer' : 'Dealer'} Worksheet`}
                        style={{
                            width: '100%',
                            height: '100%'
                        }}
                    />
                </div>
            </div>
        </BaseModal>
    )
}
